import React, { useMemo, useState } from "react";
import FeatherIcon from "feather-icons-react";
import { HELP_CENTER_TOPICS } from "../../components/Help/helpContent";
import heroIllustration from "../../assets/images/help/help-hero-illustration-source.png";
import "./Help.scss";

const knowledgeCategories = [
  {
    id: "engagements",
    icon: "briefcase",
    title: "Engagements",
    description:
      "Create engagements, manage teams, scopes, services and review workflow.",
    count: "18 articles",
  },
  {
    id: "inspection-workspace",
    icon: "clipboard",
    title: "Checklists & Reviews",
    description:
      "Manage checklists, review questions, responses, findings and conclusions.",
    count: "24 articles",
  },
  {
    id: "documents",
    icon: "file-text",
    title: "Documents & Workpapers",
    description:
      "Upload, organize and manage workpapers with permissions and folder structures.",
    count: "16 articles",
  },
  {
    id: "reports",
    icon: "bar-chart-2",
    title: "Reports",
    description:
      "Generate reports, firm quality reports and engagement reports.",
    count: "14 articles",
  },
  {
    id: "firm-administration",
    icon: "settings",
    title: "Administration",
    description:
      "Firm settings, users, templates, SSO, subscription and permissions.",
    count: "22 articles",
  },
];

const quickAccess = [
  {
    icon: "send",
    tone: "purple",
    title: "Get Started",
    text: "New to PIERS? Start here with the basics.",
    link: "Learn the basics",
  },
  {
    icon: "book-open",
    tone: "green",
    title: "Browse Knowledge Base",
    text: "Find answers to common questions and how-to articles.",
    link: "Explore articles",
  },
  {
    icon: "compass",
    tone: "blue",
    title: "Guides & Tutorials",
    text: "Step-by-step guides and video walkthroughs.",
    link: "View guides",
  },
  {
    icon: "headphones",
    tone: "orange",
    title: "Contact Support",
    text: "Can't find what you need? We're here to help.",
    link: "Submit a request",
  },
];

const blogPosts = [
  {
    tag: "Product",
    tone: "violet",
    title: "What's New in PIERS – August 2026",
    text: "Explore the latest features, improvements and enhancements released this month.",
    date: "Aug 13, 2026",
    time: "5 min read",
    icon: "monitor",
  },
  {
    tag: "Best Practices",
    tone: "mint",
    title: "5 Best Practices for Effective Peer Review",
    text: "Follow these proven practices to ensure consistent and high-quality peer reviews.",
    date: "Aug 08, 2026",
    time: "6 min read",
    icon: "users",
  },
  {
    tag: "Industry",
    tone: "blue",
    title: "Ensuring Quality: The Role of Peer Review",
    text: "Why peer review is critical for CPA firms and how technology makes it better.",
    date: "Jul 30, 2026",
    time: "4 min read",
    icon: "shield",
  },
];

const fallbackArticles = [
  {
    id: "create-engagement",
    category: "Engagements",
    title: "How to Create a New Engagement",
    description:
      "Set up the client, service, team, and engagement information before work starts.",
  },
  {
    id: "review-workflow",
    category: "Checklists & Reviews",
    title: "Understanding the Review Workflow",
    description:
      "Learn how review status, queries, findings, and approval stages work together.",
  },
  {
    id: "assign-reviewer",
    category: "Checklists & Reviews",
    title: "How to Assign Reviewers to an Engagement",
    description:
      "Set the review owner, primary reviewer, approver, and additional reviewers.",
  },
  {
    id: "manage-queries",
    category: "Checklists & Reviews",
    title: "Managing Queries and Responses",
    description:
      "Track follow-up items and keep resolution evidence linked to each question.",
  },
  {
    id: "generate-report",
    category: "Reports",
    title: "How to Generate an Engagement Report",
    description:
      "Prepare a shareable report from current engagement and review information.",
  },
];

const detailedArticles = HELP_CENTER_TOPICS.flatMap((topic) =>
  topic.articles.map((article) => ({
    ...article,
    category: topic.title,
    description: article.body[0],
  }))
);

const chipSearches = [
  "Engagement Setup",
  "Checklists",
  "Review Templates",
  "Reports",
  "Queries",
];

const HelpIndex = () => {
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedArticle, setSelectedArticle] = useState(null);

  const filteredArticles = useMemo(() => {
    const searchTerm = query.trim().toLowerCase();
    return [...detailedArticles, ...fallbackArticles].filter((article) => {
      const categoryMatches =
        !selectedCategory ||
        article.categoryId === selectedCategory ||
        article.category
          .toLowerCase()
          .includes(
            selectedCategory === "firm-administration" ? "firm" : "inspection"
          );
      const text = `${article.title} ${article.category} ${
        article.description
      } ${(article.body || []).join(" ")}`.toLowerCase();
      return categoryMatches && (!searchTerm || text.includes(searchTerm));
    });
  }, [query, selectedCategory]);

  const openCategory = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedArticle(null);
    document
      .getElementById("help-articles")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="help-page">
      <section className="help-hero">
        <div className="help-hero__grid" />
        <div className="help-hero__content">
          <div className="help-hero__copy">
            <span className="help-hero__eyebrow">PIERS KNOWLEDGE CENTER</span>
            <h1>How can we help you today?</h1>
            <p>
              Find answers, step-by-step guides and expert resources to help you
              get the most out of PIERS.
            </p>
            <label className="help-hero__search" htmlFor="help-search-input">
              <FeatherIcon icon="search" />
              <input
                id="help-search-input"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search for articles, guides, or topics..."
              />
              <button
                type="button"
                onClick={() =>
                  document
                    .getElementById("help-articles")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
              >
                Search
              </button>
            </label>
            <div className="help-hero__popular">
              <span>Popular searches:</span>
              {chipSearches.map((term) => (
                <button type="button" key={term} onClick={() => setQuery(term)}>
                  {term}
                </button>
              ))}
            </div>
          </div>
          <div className="help-hero__art" aria-hidden="true">
            <span className="help-hero__orbit help-hero__orbit--one" />
            <span className="help-hero__orbit help-hero__orbit--two" />
            <img src={heroIllustration} alt="" />
          </div>
        </div>
      </section>

      <div className="help-page__container">
        <section className="help-quick-access" aria-label="Quick help options">
          {quickAccess.map((item) => (
            <article key={item.title}>
              <span
                className={`help-quick-access__icon help-quick-access__icon--${item.tone}`}
              >
                <FeatherIcon icon={item.icon} />
              </span>
              <div>
                <h2>{item.title}</h2>
                <p>{item.text}</p>
                <button
                  type="button"
                  onClick={() =>
                    item.title === "Browse Knowledge Base"
                      ? openCategory("firm-administration")
                      : document
                          .getElementById("help-articles")
                          ?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          })
                  }
                >
                  {item.link} <FeatherIcon icon="arrow-right" />
                </button>
              </div>
            </article>
          ))}
        </section>

        <section
          className="help-section"
          aria-labelledby="knowledge-base-heading"
        >
          <header className="help-section__title">
            <div>
              <span>EXPLORE</span>
              <h2 id="knowledge-base-heading">Knowledge Base</h2>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedCategory("");
                document
                  .getElementById("help-articles")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              View all categories <FeatherIcon icon="arrow-right" />
            </button>
          </header>
          <div className="help-category-grid">
            {knowledgeCategories.map((category) => (
              <button
                className={`help-category-card ${
                  selectedCategory === category.id ? "is-selected" : ""
                }`}
                type="button"
                key={category.id}
                onClick={() => openCategory(category.id)}
              >
                <span className="help-category-card__icon">
                  <FeatherIcon icon={category.icon} />
                </span>
                <strong>{category.title}</strong>
                <small>{category.description}</small>
                <em>
                  <FeatherIcon icon="file-text" /> {category.count}
                </em>
              </button>
            ))}
          </div>
        </section>

        <section className="help-content-grid" id="help-articles">
          <article className="help-panel help-popular-panel">
            <header>
              <h2>
                <FeatherIcon icon="trending-up" /> Popular Articles
              </h2>
            </header>
            <div>
              {fallbackArticles.map((article) => (
                <button
                  type="button"
                  key={article.id}
                  onClick={() => setSelectedArticle(article)}
                >
                  <span>
                    <FeatherIcon icon="file-text" /> {article.title}
                  </span>
                  <FeatherIcon icon="chevron-right" />
                </button>
              ))}
            </div>
            <button
              className="help-panel__footer-link"
              type="button"
              onClick={() => setSelectedCategory("")}
            >
              View all popular articles <FeatherIcon icon="arrow-right" />
            </button>
          </article>

          <article className="help-panel help-guides-panel">
            <header>
              <h2>
                <FeatherIcon icon="book-open" /> Guides &amp; Tutorials
              </h2>
              <button
                type="button"
                onClick={() => openCategory("inspection-workspace")}
              >
                View all guides <FeatherIcon icon="arrow-right" />
              </button>
            </header>
            <div className="help-guide-list">
              {filteredArticles.slice(0, 3).map((article, index) => (
                <button
                  type="button"
                  key={article.id}
                  onClick={() => setSelectedArticle(article)}
                >
                  <span
                    className={`help-guide-list__visual help-guide-list__visual--${
                      index + 1
                    }`}
                  >
                    <FeatherIcon
                      icon={
                        index === 0
                          ? "play-circle"
                          : index === 1
                          ? "check-circle"
                          : "file-text"
                      }
                    />
                  </span>
                  <span>
                    <strong>{article.title}</strong>
                    <small>{article.description}</small>
                    <em>{article.category}</em>
                  </span>
                </button>
              ))}
            </div>
          </article>

          <article className="help-panel help-updates-panel">
            <header>
              <h2>
                <FeatherIcon icon="clock" /> Recently Updated
              </h2>
            </header>
            <div>
              {detailedArticles
                .slice(-5)
                .reverse()
                .map((article, index) => (
                  <button
                    type="button"
                    key={article.id}
                    onClick={() => setSelectedArticle(article)}
                  >
                    <span>
                      <FeatherIcon icon="file-text" /> {article.title}
                    </span>
                    <small>{`Aug ${13 - index}, 2026`}</small>
                  </button>
                ))}
            </div>
            <button
              className="help-panel__footer-link"
              type="button"
              onClick={() => openCategory("inspection-workspace")}
            >
              View all updated articles <FeatherIcon icon="arrow-right" />
            </button>
          </article>
        </section>

        {selectedArticle && (
          <section className="help-article-detail" aria-live="polite">
            <header>
              <div>
                <span>{selectedArticle.category}</span>
                <h2>{selectedArticle.title}</h2>
              </div>
              <button
                type="button"
                onClick={() => setSelectedArticle(null)}
                aria-label="Close article"
              >
                <FeatherIcon icon="x" />
              </button>
            </header>
            {(selectedArticle.body || [selectedArticle.description]).map(
              (paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              )
            )}
          </section>
        )}

        <section
          className="help-section help-blog-section"
          aria-labelledby="latest-blog-heading"
        >
          <header className="help-section__title">
            <div>
              <span>INSIGHTS</span>
              <h2 id="latest-blog-heading">Latest from Our Blog</h2>
            </div>
            <button type="button">
              View all blogs <FeatherIcon icon="arrow-right" />
            </button>
          </header>
          <div className="help-blog-grid">
            {blogPosts.map((post) => (
              <article key={post.title}>
                <div
                  className={`help-blog-card__visual help-blog-card__visual--${post.tone}`}
                >
                  <span>{post.tag}</span>
                  <FeatherIcon icon={post.icon} />
                </div>
                <div className="help-blog-card__body">
                  <h3>{post.title}</h3>
                  <p>{post.text}</p>
                  <small>
                    <FeatherIcon icon="calendar" /> {post.date} <i />{" "}
                    <FeatherIcon icon="clock" /> {post.time}
                  </small>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          className="help-benefits"
          id="help-benefits"
          aria-label="PIERS help benefits"
        >
          {[
            [
              "search",
              "Smart Search",
              "Find answers faster with AI-powered search.",
            ],
            [
              "bookmark",
              "Save & Bookmark",
              "Save important articles for quick access anytime.",
            ],
            [
              "star",
              "Rate Articles",
              "Was this helpful? Rate articles and help us improve.",
            ],
            [
              "bell",
              "Stay Updated",
              "Subscribe to updates and never miss important news.",
            ],
          ].map(([icon, title, text]) => (
            <article key={title}>
              <span>
                <FeatherIcon icon={icon} />
              </span>
              <div>
                <strong>{title}</strong>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </section>
      </div>
    </main>
  );
};

export default HelpIndex;
